import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, User, Store, Bike } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { key: "customer", label: "Customer", icon: User, desc: "Order food & more" },
  { key: "restaurant", label: "Restaurant Partner", icon: Store, desc: "Sell on BiteGoFood (KYC required)" },
  { key: "delivery", label: "Delivery Partner", icon: Bike, desc: "Deliver & earn (KYC required)" },
];

export default function AuthCallback() {
  const nav = useNavigate();
  const { login } = useAuth();
  const processed = useRef(false);
  const [needsRole, setNeedsRole] = useState(false);
  const [pending, setPending] = useState(null);
  const [profile, setProfile] = useState(null);
  const [working, setWorking] = useState(false);

  const finish = (data) => {
    login(data.token, data.user);
    const u = data.user;
    if (u.role === "restaurant") return nav(u.active ? "/partner" : "/partner/register");
    if (u.role === "delivery") return nav(u.active || u.kyc_verified ? "/rider" : "/rider/register");
    nav("/");
  };

  const complete = async (role) => {
    setWorking(true);
    try {
      const { data } = await api.post("/auth/google/complete", { pending_token: pending, role });
      localStorage.removeItem("bitego_google_role");
      toast.success("Account created");
      finish(data);
    } catch (e) { toast.error(apiError(e)); setWorking(false); }
  };

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) { nav("/login"); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sid });
        window.history.replaceState(null, "", "/auth/callback");
        if (!data.needs_role) { finish(data); return; }
        setPending(data.pending_token);
        setProfile(data.profile);
        const preset = localStorage.getItem("bitego_google_role");
        if (preset === "restaurant" || preset === "delivery") {
          const { data: c } = await api.post("/auth/google/complete", { pending_token: data.pending_token, role: preset });
          localStorage.removeItem("bitego_google_role");
          finish(c);
          return;
        }
        setNeedsRole(true);
      } catch (e) { toast.error(apiError(e)); nav("/login"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!needsRole) {
    return <div className="min-h-screen bg-surface flex flex-col items-center justify-center gap-3"><Loader2 className="animate-spin text-coral" size={28} /><p className="text-sm text-ink-600">Signing you in…</p></div>;
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col justify-center max-w-[520px] mx-auto px-5 py-6" data-testid="google-role-select">
      <div className="flex flex-col items-center"><img src="/bitego-icon.png" alt="BiteGoFood" className="w-14 h-14 rounded-2xl shadow" /><h1 className="mt-2 font-display font-extrabold text-xl text-ink">Welcome{profile?.name ? `, ${profile.name.split(" ")[0]}` : ""}!</h1><p className="text-ink-600 text-sm">How would you like to use BiteGoFood?</p></div>
      <div className="mt-6 space-y-2">
        {ROLES.map((r) => (
          <button key={r.key} disabled={working} onClick={() => complete(r.key)} className="w-full bg-white border border-border rounded-2xl p-4 flex items-center gap-3 text-left tap hover:border-coral" data-testid={`google-role-${r.key}`}>
            <div className="w-10 h-10 rounded-xl bg-coral-light flex items-center justify-center"><r.icon className="text-coral" size={20} /></div>
            <div><div className="font-bold text-ink">{r.label}</div><div className="text-xs text-ink-600">{r.desc}</div></div>
          </button>
        ))}
      </div>
      {working && <div className="mt-4 flex justify-center"><Loader2 className="animate-spin text-coral" size={22} /></div>}
      <p className="text-center text-[11px] text-ink-600 mt-6">A Product by Vijay Software Solutions Pvt Ltd</p>
    </div>
  );
}
